import { request } from '@/shared/api/request'
import type { ApiType, UsageType, PaginatedResponse } from '@/api/common-types'
import { API_TYPE_OPTIONS, USAGE_TYPE_OPTIONS } from '@/api/api-constants'

export interface APIConfig {
    id: number
    name: string
    url: string
    api_key_masked: string
    api_type: ApiType
    usage_type: UsageType
    status: boolean
    description?: string
    region?: string
    resource_name?: string
    api_version?: string
    model_name?: string
    temperature?: number
    max_tokens?: number | null
    timeout?: number
    retry_count?: number
    extra_config?: Record<string, any>
    created_at: string
    updated_at: string
}

export interface APIConfigCreate {
    name: string
    url: string
    api_key: string
    api_type: ApiType
    usage_type: UsageType
    status: boolean
    description?: string
    region?: string
    resource_name?: string
    api_version?: string
    model_name?: string
    temperature?: number
    max_tokens?: number | null
    timeout?: number
    retry_count?: number
    extra_config?: Record<string, any>
}

export interface APIConfigUpdate {
    name?: string
    url?: string
    api_key?: string
    api_type?: ApiType
    usage_type?: UsageType
    status?: boolean
    description?: string
    region?: string
    resource_name?: string
    api_version?: string
    model_name?: string
    temperature?: number
    max_tokens?: number | null
    timeout?: number
    retry_count?: number
    extra_config?: Record<string, any>
}

export interface APIConfigStats {
    total_apis: number
    active_apis: number
    inactive_apis: number
    today_calls?: number
    by_type: Record<string, number>
    by_usage_type: Record<string, number>
}

export interface APICallStats {
    total_calls: number
    success_calls: number
    failed_calls: number
    success_rate: number
    avg_response_time: number
    total_usage: number
    calls_by_api: Record<string, number>
    calls_by_date: Record<string, number>
}

export interface APITestRequest {
    api_id: number
    content: string
    source_lang?: string
    target_lang?: string
    extra_params?: Record<string, any>
}

export interface APITestResponse {
    success: boolean
    response_time: number
    status_code: number
    usage_amount?: number
    response_data: string
    error_message?: string
}

export interface APIConfigListResponse {
    items: APIConfig[]
    total: number
    page: number
    size: number
    pages: number
}

// API配置管理接口
export const apiConfigApi = {
    // 获取API配置列表
    getApiConfigs: (params: {
        page?: number
        size?: number
        api_type?: string
        status?: boolean
        search?: string
    }) => {
        return request.get<APIConfigListResponse>('/api-configs/', { params })
    },

    // 获取API配置统计
    getApiStats: () => {
        return request.get<APIConfigStats>('/api-configs/stats')
    },

    // 获取单个API配置
    getApiConfig: (id: number) => {
        return request.get<APIConfig>(`/api-configs/${id}`)
    },

    // 创建API配置
    createApiConfig: (data: APIConfigCreate) => {
        return request.post<APIConfig>('/api-configs/', data)
    },

    // 更新API配置
    updateApiConfig: (id: number, data: APIConfigUpdate) => {
        return request.put<APIConfig>(`/api-configs/${id}`, data)
    },

    // 删除API配置
    deleteApiConfig: (id: number) => {
        return request.delete(`/api-configs/${id}`)
    },

    // 切换API配置状态
    toggleApiStatus: (id: number) => {
        return request.post<APIConfig>(`/api-configs/${id}/toggle`)
    },

    // 测试API配置
    testApiConfig: (data: APITestRequest) => {
        return request.post<APITestResponse>('/api-configs/test', data)
    },

    // 获取API调用统计
    getApiCallStats: (id: number, days: number = 30) => {
        return request.get<APICallStats>(`/api-configs/${id}/stats`, {
            params: { days }
        })
    },

    // 根据类型获取API配置
    getApisByType: (apiType: string, activeOnly: boolean = true) => {
        return request.get<APIConfig[]>(`/api-configs/type/${apiType}`, {
            params: { active_only: activeOnly }
        })
}}

// ===== 兼容性导出：直接函数形式 =====
// 保留原有的直接函数导出以确保向后兼容

export const getApiConfigs = apiConfigApi.getApiConfigs
export const getApiStats = apiConfigApi.getApiStats
export const getApiConfig = apiConfigApi.getApiConfig
export const createApiConfig = apiConfigApi.createApiConfig
export const updateApiConfig = apiConfigApi.updateApiConfig
export const deleteApiConfig = apiConfigApi.deleteApiConfig
export const toggleApiStatus = apiConfigApi.toggleApiStatus
export const testApiConfig = apiConfigApi.testApiConfig
export const getApiCallStats = apiConfigApi.getApiCallStats
export const getApisByType = apiConfigApi.getApisByType

// 常量导出
export { API_TYPE_OPTIONS, USAGE_TYPE_OPTIONS }